export type ReadAs = "whole" | "jsonField";
export type SaveTo = "laterRequests" | "assistant";

export type ReturnParam = {
  _id?: any;
  paramName: string;
  persistParam: boolean;
  useLocation: "prompt" | "API";
  returnType: "text" | "object";
  paramValue?: string;
  [key: string]: any;
};

export type ResponseField = {
  name: string;
  readAs: ReadAs;
  /** JSON path when readAs is "jsonField", e.g. order.confirmationCode */
  path: string;
  saveTo: SaveTo;
  raw?: ReturnParam;
};

export const SAVE_TO_OPTIONS: Array<{ value: SaveTo; label: string; description: string }> = [
  {
    value: "laterRequests",
    label: "For later requests",
    description: "Any API that runs later in this call can send it as a field. The assistant never sees it.",
  },
  {
    value: "assistant",
    label: "For the assistant to use",
    description: "The assistant can read it. Refer to it in the prompt or in spoken messages with curly brackets.",
  },
];

export const READ_AS_OPTIONS: Array<{ value: ReadAs; label: string }> = [
  { value: "whole", label: "The whole response" },
  { value: "jsonField", label: "One field from the reply" },
];

export function newResponseField(): ResponseField {
  return { name: "", readAs: "whole", path: "", saveTo: "assistant" };
}

export function fromReturnParam(param: ReturnParam): ResponseField {
  return {
    name: param.paramName ?? "",
    readAs: param.returnType === "object" ? "jsonField" : "whole",
    path: param.paramValue ?? "",
    saveTo: param.useLocation === "API" ? "laterRequests" : "assistant",
    raw: param,
  };
}

export function toReturnParam(field: ResponseField): ReturnParam {
  const base: Partial<ReturnParam> = field.raw ? { ...field.raw } : {};
  return {
    ...base,
    paramName: (field.name ?? "").trim(),
    persistParam: true,
    useLocation: field.saveTo === "laterRequests" ? "API" : "prompt",
    returnType: field.readAs === "jsonField" ? "object" : "text",
    paramValue: field.readAs === "jsonField" ? (field.path ?? "").trim() : "",
  } as ReturnParam;
}

/** A stored row that the backend currently ignores because it was never marked to be saved. */
export function isLegacyUnsaved(param: ReturnParam | undefined | null): boolean {
  return !!param && !param.persistParam;
}
